/**
 * fetchAudienceInsight.js
 * ---------------------------------------------------------------
 * Identifies the demographic sub-groups with the highest average
 * response for the active TargetVariable1. Checks all individual
 * dimensions first, then scores the top-3 dimension pairs.
 *
 * Extracted from MapPage.fetchTopDemographicInsight.
 *
 * @param {object}   params.props          - MapPage props
 * @param {object}   params.state          - MapPage state snapshot
 * @param {function} params.getActiveGroup - returns the active group object
 * @param {object}   params._tracking      - { key, abort } mutated in place for dedup/cancel
 */
import { tableFromIPC } from "apache-arrow";
import { isTrackingSchema, schemaRequestFields } from "../schemaTypes";
import { rowsFromTable } from "./helpers";
import { DEMO_LABELS } from "../demographicLabels";
import { weightColForGroup, weightedMean } from "../keyWeights";

// Weighted-base column returned on every row by both data backends.
const BASE_COL = "n_raked";
// Minimum share of an area's base a demographic sub-group must hold for that
// area to count toward its score. Without a floor, thin slices such as
// "Independent/Other" 2024 voters — a few per cent almost everywhere — win the
// sweep on a handful of respondents.
const MIN_SUBGROUP_SHARE_PCT = 10;

export async function fetchAudienceInsight({ props, state, getActiveGroup, _tracking }) {
  if (props.ActionMode !== "filter") return;
  if (!props.onAudienceInsightChange) return;

  const group = getActiveGroup?.();
  const isLikertGridTarget = group?.TargetVariable1?.type === "likert_grid";
  if (!group?.TargetVariable1 || (!isLikertGridTarget && !group?.TargetVariable1?.keys?.length)) return;

  const subVar = props.ActionTargetSubVariables?.TargetSubVariable1;
  if (!subVar) return;
  const [subVarId, subMetricRaw] = String(subVar).split("|");
  const targetSubGroup = isLikertGridTarget ? props.groupsById?.[subVarId] : null;
  if (isLikertGridTarget && !targetSubGroup) return;

  const constituencyCode =
    state.selectedConstituencyCode ||
    props.selectedconstCode ||
    (state.selectedFeature?.level === "constituency" ? state.selectedFeature?.value : null);

  // Match the same level the map is currently showing:
  // - ward level when drilled into a constituency (areaCodes filters to all wards in it)
  // - constituency or local authority nationally (areaCodes empty = every area)
  const level = state.geoLevel === "local_authority" ? "local_authority"
    : state.geoLevel === "ward" ? "ward"
    : "constituency";
  const areaCodes = level === "ward" && constituencyCode ? [constituencyCode] : [];

  const theme = props.selectedSection || "pol";
  // An active segment filter (e.g. energy provider customers) scopes the whole
  // sweep, so every candidate demographic is scored within that segment.
  const segment = [].concat(props.segmentFilter || []).filter(Boolean);
  const key = `${level}|${constituencyCode || "*"}|${theme}|${subVar}|${props.ActionTargetVariables?.TargetVariable1}|${segment.join(",")}`;

  if (_tracking.key === key) return;
  _tracking.key = key;

  if (_tracking.abort) _tracking.abort.abort();
  _tracking.abort = new AbortController();
  props.onAudienceInsightLoadingChange?.(true);

  const toArray = (v) => (Array.isArray(v) ? v : v != null ? [v] : []);
  const posKeys = toArray(
    isLikertGridTarget ? targetSubGroup?.polarity?.pos : group?.TargetVariable1?.polarity?.pos
  );
  const negKeys = toArray(
    isLikertGridTarget ? targetSubGroup?.polarity?.neg : group?.TargetVariable1?.polarity?.neg
  );
  const metricToken = String(subMetricRaw || subVar || "").toLowerCase();
  const metricMode = ["distrust", "discomfort", "neg", "negative"].includes(metricToken)
    ? "neg"
    : ["trust", "comfort", "pos", "positive"].includes(metricToken)
      ? "pos"
      : metricToken;
  const groupKeys = (() => {
    if (isLikertGridTarget) {
      if (metricMode === "pos") return Array.from(new Set(posKeys));
      if (metricMode === "neg") return Array.from(new Set(negKeys));
      if (metricMode === "net") return Array.from(new Set([...posKeys, ...negKeys]));
      return [subMetricRaw].filter(Boolean);
    }
    return subVar === "net" ? [...posKeys, ...negKeys] : [subVar];
  })();

  // Use the target variable's schema (same logic as fetchMetrics in filter mode).
  // group.schema may be undefined in filter mode — the variable's own schema is
  // on TargetVariable1 (or the likert_grid sub-group).
  const schema = isLikertGridTarget
    ? (targetSubGroup?.schema || group?.TargetVariable1?.schema || group?.schema)
    : (group?.TargetVariable1?.schema || group?.schema);

  const baseBody = {
    areaCodes,
    theme,
    groupKeys,
    filters: {
      ...(isTrackingSchema(schema) && props.waveSelect != null ? { Wave: props.waveSelect } : {}),
      ...(segment.length ? { segment } : {}),
    },
    ...schemaRequestFields(schema),
    group_by_wave: false,
    normalise_over_options: false,
  };

  const sumKeys = (keys, row) => keys.reduce((acc, k) => acc + (Number(row?.[k]) || 0), 0);

  const responseValue = (row) => {
    if (isLikertGridTarget) {
      const pos = sumKeys(posKeys, row);
      const neg = sumKeys(negKeys, row);
      if (metricMode === "pos") return Number.isFinite(pos) ? pos : null;
      if (metricMode === "neg") return Number.isFinite(neg) ? neg : null;
      if (metricMode === "net") {
        const hasData = [...posKeys, ...negKeys].some((k) => Number.isFinite(Number(row?.[k])));
        return hasData ? pos - neg : null;
      }
      const v = Number(row?.[subMetricRaw]);
      return Number.isFinite(v) ? v : null;
    }
    if (subVar === "net") {
      const pos = sumKeys(posKeys, row);
      const neg = sumKeys(negKeys, row);
      const hasData = [...posKeys, ...negKeys].some((k) => Number.isFinite(Number(row?.[k])));
      return hasData ? pos - neg : null;
    }
    const v = Number(row?.[subVar]);
    return Number.isFinite(v) ? v : null;
  };

  // Score each demographic filter by the weighted mean across the full current
  // scope. This keeps the targeting workflow consistent with the main filtered
  // map/question values:
  // - national scope -> all filtered constituencies
  // - constituency drilldown -> all filtered wards in that constituency
  // responseValue reads one group's keys, so the whole average shares that
  // group's weight column: n_voters for vote share, n_raked otherwise.
  const insightWeightCol = weightColForGroup(group);

  // Every row carries n_raked (the weighted base) alongside the percentages, so
  // a sub-group's share of an area is its own base over that area's total.
  const areaKeyOf = (r) =>
    String(r?.area_cd ?? r?.area_id ?? r?.PCON24CD ?? r?.WD24CD ?? "");

  const scoreRows = (rows, baseByArea) => {
    // Areas where the sub-group holds less than MIN_SUBGROUP_SHARE_PCT of the
    // base are dropped before averaging: a handful of respondents produces a
    // wild average that would otherwise win the sweep. A sub-group that clears
    // the floor nowhere scores null and drops out of the sweep entirely.
    const useShareFloor = baseByArea && baseByArea.size > 0;
    const kept = [];
    let withData = 0, shareNum = 0, shareDen = 0;
    for (const r of rows || []) {
      if (!Number.isFinite(responseValue(r))) continue;
      withData += 1;
      if (useShareFloor) {
        const n = Number(r?.[BASE_COL]);
        const total = baseByArea.get(areaKeyOf(r));
        if (Number.isFinite(n) && Number.isFinite(total) && total > 0) {
          shareNum += n;
          shareDen += total;
          if ((n / total) * 100 < MIN_SUBGROUP_SHARE_PCT) continue;
        }
      }
      kept.push(r);
    }
    return {
      // The surviving areas are averaged with the group's weight column, so the
      // share floor composes with the weighting rather than replacing it.
      score: kept.length ? weightedMean(kept, responseValue, insightWeightCol) : null,
      areasKept: kept.length,
      areasWithData: withData,
      // Share across the whole scope, for explaining an exclusion.
      sharePct: shareDen > 0 ? (shareNum / shareDen) * 100 : null,
      // Had data everywhere, but nowhere big enough to count.
      belowFloor: useShareFloor && withData > 0 && kept.length === 0,
    };
  };

  // Fetch Arrow data for a given body, returning parsed rows or null on failure
  const postArrow = async (body) => {
    const res = await fetch(`/api/${level}_data_arrow/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal: _tracking.abort.signal,
    });
    if (!res.ok) return null;
    return rowsFromTable(tableFromIPC(await res.arrayBuffer()));
  };

  const candidates = {
    gender:    [2, 1],
    age:       [1, 2, 3],
    education: [1, 2],
    ethnicity: [1, 2],
    party:     [1, 2, 3, 4, 5, 6, 7, 8, 9],
  };

  const labelFor = (dim, val) => DEMO_LABELS?.[dim]?.[val] || String(val);

  try {
    // Unfiltered baseline for the same scope, wave and segment — the denominator
    // for each sub-group's share. If it fails we score without the floor rather
    // than excluding everything.
    const baselineRows = await postArrow(baseBody);
    const baseByArea = new Map();
    for (const r of baselineRows || []) {
      const k = areaKeyOf(r);
      const n = Number(r?.[BASE_COL]);
      if (k && Number.isFinite(n)) baseByArea.set(k, (baseByArea.get(k) || 0) + n);
    }
    const shareFloorActive = baseByArea.size > 0;
    if (!shareFloorActive) {
      console.warn("audience insight: no baseline bases returned, scoring without the share floor");
    }

    // Step 1: find the best single value per demographic dimension
    const bestByDim = {};
    const excludedSmall = [];
    for (const [dim, values] of Object.entries(candidates)) {
      let bestDim = null;
      for (const val of values) {
        if (_tracking.abort?.signal?.aborted) return;
        const rows = await postArrow({ ...baseBody, filters: { ...baseBody.filters, [dim]: val } });
        if (!rows) continue;
        const res = scoreRows(rows, baseByArea);
        if (res.belowFloor) {
          excludedSmall.push({
            dim,
            val,
            label: labelFor(dim, val),
            sharePct: res.sharePct,
          });
          continue;
        }
        if (!Number.isFinite(res.score)) continue;
        if (!bestDim || res.score > bestDim.score) {
          bestDim = { dim, val, score: res.score, sharePct: res.sharePct, areasKept: res.areasKept };
        }
      }
      if (bestDim) bestByDim[dim] = bestDim;
    }

    // Step 2: test combined pairs from the top-3 scoring dimensions
    let best = null;
    const topPairs = [];
    const topDims = Object.entries(bestByDim)
      .sort((a, b) => (b[1]?.score ?? -Infinity) - (a[1]?.score ?? -Infinity))
      .map(([dim]) => dim)
      .slice(0, 3);

    for (let i = 0; i < topDims.length; i++) {
      for (let j = i + 1; j < topDims.length; j++) {
        if (_tracking.abort?.signal?.aborted) return;
        const dimA = topDims[i], dimB = topDims[j];
        const valA = bestByDim[dimA]?.val, valB = bestByDim[dimB]?.val;
        if (valA == null || valB == null) continue;
        const rows = await postArrow({ ...baseBody, filters: { ...baseBody.filters, [dimA]: valA, [dimB]: valB } });
        if (!rows) continue;
        // Pairs are scored without the floor: an intersection of two groups that
        // each clear 10% is often under 10% itself, so applying it here would
        // remove nearly every pair. The floor still governs which values can be
        // paired at all, since pairs come from the Step 1 winners.
        const res = scoreRows(rows, null);
        if (!Number.isFinite(res.score)) continue;
        const candidate = { dimA, valA, dimB, valB, score: res.score };
        if (!best || res.score > best.score) best = candidate;
        topPairs.push(candidate);
      }
    }

    if (!best && Object.keys(bestByDim).length === 0) {
      props.onAudienceInsightLoadingChange?.(false);
      return;
    }

    const dimLabel = (dim) => dim.charAt(0).toUpperCase() + dim.slice(1);
    const responseLabel = (() => {
      if (isLikertGridTarget) {
        const firstPos = posKeys[0];
        const firstNeg = negKeys[0];
        const posLabel = targetSubGroup?.labels?.[firstPos] || "Trust";
        const negLabel = targetSubGroup?.labels?.[firstNeg] || "Distrust";
        if (metricMode === "pos") return posLabel;
        if (metricMode === "neg") return negLabel;
        if (metricMode === "net") return "Target net";
        return targetSubGroup?.labels?.[subMetricRaw] || subMetricRaw || "Response";
      }
      return subVar === "net"
        ? "Net"
        : (group?.TargetVariable1?.full_labels?.[subVar] || group?.TargetVariable1?.labels?.[subVar] || subVar);
    })();

    const topPairsSorted = topPairs
      .filter((p) => Number.isFinite(p.score))
      .sort((a, b) => b.score - a.score)
      .slice(0, 3)
      .map((p) => ({ label: `${labelFor(p.dimA, p.valA)} + ${labelFor(p.dimB, p.valB)}`, score: p.score }));

    props.onAudienceInsightChange({
      responseLabel,
      combined: best
        ? {
            dimension: `${dimLabel(best.dimA)} + ${dimLabel(best.dimB)}`,
            label: `${labelFor(best.dimA, best.valA)} + ${labelFor(best.dimB, best.valB)}`,
            score: best.score,
          }
        : null,
      topPairs: topPairsSorted,
      perDimension: Object.fromEntries(
        Object.entries(bestByDim).map(([dim, info]) => [
          dim,
          { label: labelFor(dim, info.val), score: info.score, sharePct: info.sharePct },
        ])
      ),
      // Surfaced so a dropped sub-group reads as a deliberate exclusion rather
      // than missing data.
      minSharePct: shareFloorActive ? MIN_SUBGROUP_SHARE_PCT : null,
      excludedSmall: excludedSmall.map((e) => e.label),
      summary: best
        ? `Strongest combined audience for ${responseLabel}: ${labelFor(best.dimA, best.valA)} + ${labelFor(best.dimB, best.valB)} (${best.score.toFixed(1)}%)`
        : null,
    });
    props.onAudienceInsightLoadingChange?.(false);
  } catch (e) {
    if (e.name !== "AbortError") console.error("audience insight error:", e);
    props.onAudienceInsightLoadingChange?.(false);
  }
}
